import { Tabs, TabsList, TabsTrigger } from '@liza/ui/components/ui/tabs';
import { useScheduleTypeLabels } from '@liza/ui/hooks/useScheduleTypeLabels';
import { SHARED_SCHEDULE_TYPES, type ScheduleType } from '@liza/ui/utils/schedulePicker';
import { useTranslation } from 'react-i18next';

/** Presentation order for every surface — the tabs render in this order and filter to the modes the
 *  host form offers, so no form can drift into its own ordering. `SHARED_SCHEDULE_TYPES` is itself the
 *  agreed frequency order (recurring modes, then As needed, then Once), so the option set and the tab
 *  order are the same list rather than two that have to be kept in step (#8989). */
const ORDERED_SCHEDULE_TYPES: readonly ScheduleType[] = SHARED_SCHEDULE_TYPES;

/** Single-select between the schedule modes, in canonical order, showing only the modes the host form offers. */
export function ScheduleTypeTabs({
  value,
  visibleTypes,
  onValueChange,
}: {
  value: ScheduleType;
  visibleTypes: readonly ScheduleType[];
  onValueChange: (value: ScheduleType) => void;
}): React.ReactNode {
  const { t } = useTranslation('common');
  const labels = useScheduleTypeLabels();
  return (
    <Tabs value={value} onValueChange={(v) => onValueChange(v as ScheduleType)}>
      {/* Five modes don't fit the 512px care-plan modal at the trigger's natural width, and the
          triggers are `whitespace-nowrap` so they can't shrink to fit. Let the labels wrap to two
          lines and give the row an auto height instead of scrolling or truncating — every mode stays
          visible and hit-testable, which matters most on the narrowest surface (#8100). */}
      <TabsList className="w-full group-data-[orientation=horizontal]/tabs:h-auto" aria-label={t('scheduleFrequency')}>
        {ORDERED_SCHEDULE_TYPES.filter((type) => visibleTypes.includes(type)).map((type) => (
          <TabsTrigger
            key={type}
            value={type}
            className="h-auto min-w-0 flex-col px-1 text-center text-xs whitespace-normal"
            data-testid={`schedule-mode-${type}`}
          >
            {labels[type]}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
